import { useMutation } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { ServerCrash, UserRound } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { Toast } from '@/common/components/Toast'
import { Gender } from '@/common/models/person'
import { UserType } from '@/common/models/user'
import { api, ApiError } from '@/lib/axios'
import { queryClient } from '@/lib/tanstack'
import { NewUserForm } from '../schemas/NewUserSchema'

type PostNewUserProps = {
  allowedPosts: boolean
  changePasswordOnNextLogin: false
  displayName: string
  email: string
  isUserFirstLogin: false
  person: {
    email: string
    genre: Gender
    isForeign: boolean
    birthDate?: Date
    documentNumber?: string
    name?: string
  }
  personId: null
  userType: UserType
}

async function postNewUser(data: PostNewUserProps) {
  return api.backend.post('/user', data)
}

export function useNewUser() {
  const { t } = useTranslation('registers')

  const { mutateAsync, isPending } = useMutation<
    unknown,
    AxiosError<ApiError>,
    PostNewUserProps
  >({
    mutationKey: ['/user'],
    mutationFn: postNewUser,
    onSuccess: () => {
      toast(Toast, {
        data: {
          type: 'success',
          icon: UserRound,
          title: t('users.drawer_new_user.toast_success.title'),
        },
      })
      queryClient.invalidateQueries({ queryKey: ['/user/paginate'] })
    },
    onError: (error) => {
      toast(Toast, {
        data: {
          type: 'danger',
          icon: ServerCrash,
          title: t('users.drawer_new_user.toast_error.title'),
          description:
            error?.response?.data?.friendlyMessage ||
            t('users.drawer_new_user.toast_error.description'),
        },
      })
    },
  })

  const handleCreateUser = async (data: NewUserForm) => {
    const {
      allowedPosts,
      displayName,
      email,
      genre,
      isForeign,
      birthDate,
      documentNumber,
      name,
      userType,
    } = data

    const payload: PostNewUserProps = {
      allowedPosts,
      changePasswordOnNextLogin: false,
      displayName,
      email,
      isUserFirstLogin: false,
      person: {
        email,
        genre,
        isForeign,
        birthDate,
        documentNumber,
        name,
      },
      personId: null,
      userType,
    }

    return mutateAsync(payload)
  }

  return { handleCreateUser, isCreatingUser: isPending }
}
